import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FormInput,
  FormRoot,
  FormTextarea,
  RadioGroup,
  RadioGroupItem,
  RHF,
  cn,
  toast,
  zodResolver,
} from "@devhop/ui";
import { CalendarOff, Briefcase } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { useCreateDayOff } from "../api/queries";

const DayOffFormSchema = z.object({
  date: z.string().min(1, "ກະລຸນາໃສ່ວັນທີ"),
  type: z.enum(["holiday", "special_work"]),
  description: z.string().optional().nullable(),
});

type DayOffFormValues = z.infer<typeof DayOffFormSchema>;

type DayOffAddDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  year: number;
  onSuccess?: () => void;
};

export function DayOffAddDialog({
  open,
  onOpenChange,
  year,
  onSuccess,
}: DayOffAddDialogProps) {
  const createMutation = useCreateDayOff();

  const methods = RHF.useForm<DayOffFormValues>({
    defaultValues: {
      date: "",
      type: "holiday",
      description: undefined,
    },
    resolver: zodResolver(DayOffFormSchema),
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        date: "",
        type: "holiday",
        description: undefined,
      });
    }
  }, [open, methods]);

  const onSubmit = async (values: DayOffFormValues) => {
    try {
      await createMutation.mutateAsync({
        year,
        date: values.date.trim(),
        type: values.type,
        description: values.description?.trim() || null,
      });
      toast.success("ເພີ່ມວັນພັກແລ້ວ");
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>ເພີ່ມວັນພັກ / ວັນພິເສດ</DialogTitle>
        </DialogHeader>
        <FormRoot<DayOffFormValues>
          methods={methods}
          onSubmit={onSubmit}
          className="space-y-4"
        >
          <FormInput name="date" label="ວັນທີ" type="date" requiredMark />
          <Field name="type" label="ປະເພດ" requiredMark>
            <RHF.Controller
              control={methods.control}
              name="type"
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="grid grid-cols-2 gap-3"
                >
                  <label
                    htmlFor="dayoff-type-holiday"
                    className={cn(
                      "flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors",
                      field.value === "holiday"
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-muted-foreground/50 hover:bg-muted/50",
                    )}
                  >
                    <RadioGroupItem
                      value="holiday"
                      id="dayoff-type-holiday"
                      className="sr-only"
                    />
                    <CalendarOff
                      className={cn(
                        "size-8",
                        field.value === "holiday"
                          ? "text-primary"
                          : "text-muted-foreground",
                      )}
                    />
                    <span className="font-medium text-sm">ວັນພັກ</span>
                  </label>
                  <label
                    htmlFor="dayoff-type-special"
                    className={cn(
                      "flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors",
                      field.value === "special_work"
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-muted-foreground/50 hover:bg-muted/50",
                    )}
                  >
                    <RadioGroupItem
                      value="special_work"
                      id="dayoff-type-special"
                      className="sr-only"
                    />
                    <Briefcase
                      className={cn(
                        "size-8",
                        field.value === "special_work"
                          ? "text-primary"
                          : "text-muted-foreground",
                      )}
                    />
                    <span className="font-medium text-sm">ວັນພິເສດ</span>
                  </label>
                </RadioGroup>
              )}
            />
          </Field>
          <FormTextarea
            name="description"
            label="ຄຳອະທິບາຍ"
            placeholder="ຕົວຢ່າງ: ວັນປີໃໝ່"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "ກຳລັງບັນທຶກ..." : "ເພີ່ມ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
